/**
 * An icon for every screen in the menu (the owner, 2026-10-01: "sidebar mein logo show … circle highlighted if
 * selected"). A screen's icon is found from words in its label, most specific first; a screen whose label names
 * nothing here takes its section's icon. Lucide, and for the farm and the plant the RTCOM apps' own icons (rtcomIcons),
 * so a barrel or a chiller looks in the ERP as it does on the phone; both are 24 px round-stroked line icons.
 */
import {
  AlarmClock, AppWindow, BadgeIndianRupee, Barrel, Bell, Bike, Boxes, Building2, CalendarClock, CalendarDays, ChartLine,
  ClipboardCheck, ClipboardList, Cog, Contact, Cpu, Database, Droplet, Factory, FileBarChart, FileText, FlaskConical,
  Gauge, GitBranch, HandCoins, HeartPulse, History, House, Inbox, KeyRound, Landmark, LayoutDashboard, ListChecks, Map, MapPin,
  MessageSquare, Milk, Network, Package, PackageCheck, Phone, Receipt, RefreshCw, Repeat, Route, ScanLine, Search, Settings,
  Shield, ShieldCheck, Smartphone, Snowflake, Store, Tags, Thermometer, Truck, UserCog, UserPlus, Users, Wallet, Warehouse,
  Wheat, Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { RtcomBarrel, RtcomBatch, RtcomChiller, RtcomCollection, RtcomDevice, RtcomDispatch, RtcomLedger, RtcomLocus, RtcomMajorLocus,
  RtcomQuality, RtcomReports, RtcomSop, RtcomStock, RtcomThermometer, RtcomTrace, RtcomWarehouse } from './rtcomIcons';

export type NavIcon = ComponentType<{ size?: number; className?: string }>;

export const SECTION_ICON: Record<string, NavIcon> = {
  gauge: Gauge, milk: Milk, factory: Factory, wheat: Wheat, users: Users, truck: Truck, bike: Bike, contact: Contact,
  'message-square': MessageSquare, landmark: Landmark, 'chart-line': ChartLine, shield: Shield,
  // D19 phase 21: the workspaces' own (house for Home, building for B2B).
  house: House, building: Building2,
};

// Most specific first: the first rule whose words appear in the label wins.
const RULES: [RegExp, NavIcon][] = [
  // The farm and the plant: the apps' own marks.
  [/major loc/i, RtcomMajorLocus],
  [/minor loc|locus|loci/i, RtcomLocus],
  [/barrel/i, RtcomBarrel],
  [/chiller|cold room/i, RtcomChiller],
  [/collection/i, RtcomCollection],
  [/batch/i, RtcomBatch],
  [/quality/i, RtcomQuality],
  [/dispatch|transit|inward/i, RtcomDispatch],
  [/^trace$|farm trace|plant trace/i, RtcomTrace],
  [/inventory|stock/i, RtcomStock],
  [/warehouse|store/i, RtcomWarehouse],
  [/sop/i, RtcomSop],
  [/ledger/i, RtcomLedger],
  [/figures|farm reports/i, RtcomReports],
  [/plant devices|farm devices/i, RtcomDevice],
  [/temperature/i, RtcomThermometer],
  [/control center|dashboard|today/i, LayoutDashboard],
  [/attention|exception|approval/i, Inbox],
  [/notification|message/i, Bell],
  [/order book|^orders?$|order detail/i, ClipboardList],
  [/rider|driver/i, Bike],
  [/round claim|round|trip|route|stop|planning|plan and cutoff/i, Route],
  [/map/i, Map],
  [/bottle|custody|trace/i, Milk],
  [/report|performance|figures|analytics|report center/i, FileBarChart],
  [/attendance|shift/i, CalendarClock],
  [/calendar|pattern|dated/i, CalendarDays],
  [/device|phone/i, Smartphone],
  [/sync|reconcil|integration/i, RefreshCw],
  [/application|app\b|customer app/i, AppWindow],
  [/api key|maps and/i, KeyRound],
  [/staff|people|user|person/i, Users],
  [/onboard|invitation/i, UserPlus],
  [/minor loc|major loc|loci|locus|farm/i, MapPin],
  [/collection/i, Droplet],
  [/batch/i, Boxes],
  [/quality|test/i, FlaskConical],
  [/chiller|cold/i, Snowflake],
  [/temperature/i, Thermometer],
  [/barrel/i, Barrel],
  [/dispatch|transit|inward|delivery/i, Truck],
  [/production|filling|process/i, Factory],
  [/lot/i, PackageCheck],
  [/inventory|stock|store|warehouse/i, Warehouse],
  [/return/i, Repeat],
  [/proof/i, ScanLine],
  [/move|correction/i, GitBranch],
  [/subscription/i, Repeat],
  [/customer|master/i, Contact],
  [/payment|payable|payout|incentive|salary|payroll/i, HandCoins],
  [/ledger|outstanding|billing|invoice|statement/i, Receipt],
  [/price|cost/i, BadgeIndianRupee],
  [/finance|bank|journal/i, Landmark],
  [/wallet|advance|reimburse/i, Wallet],
  [/review|complaint|feedback|whatsapp|communication/i, MessageSquare],
  [/vehicle|fuel|maintenance|inspection/i, Wrench],
  [/asset/i, Package],
  [/document|sop|training/i, FileText],
  [/task|target|assignment/i, ListChecks],
  [/role|permission|security|audit/i, ShieldCheck],
  [/setting|config/i, Settings],
  [/data health|health/i, HeartPulse],
  [/history|log/i, History],
  [/search/i, Search],
  [/department|team|location|site/i, Building2],
  [/catalogue|variant|product|tag/i, Tags],
  [/alarm/i, AlarmClock],
  [/check/i, ClipboardCheck],
  [/system|admin/i, Cog],
  [/network|node/i, Network],
  [/hardware|iot|sensor/i, Cpu],
  [/database/i, Database],
  [/call|contact/i, Phone],
  [/shop|b2b/i, Store],
  [/user role/i, UserCog],
];

export function screenIcon(label: string, sectionIcon: string): NavIcon {
  return RULES.find(([re]) => re.test(label))?.[1] ?? SECTION_ICON[sectionIcon] ?? LayoutDashboard;
}
